import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useDash } from "../lib/dash";
import { AdminAreaGate } from "../components/AdminAreaGate";
import { DashHeader } from "../components/admin/DashHeader";
import { DashTabs, TABS } from "../components/admin/DashTabs";
import { TileState } from "../components/admin/Tile";
import { OverviewTab } from "../components/admin/OverviewTab";
import { AiCostsTile } from "../components/admin/AiCostsTile";
import { SalesTile } from "../components/admin/SalesTile";
import { DucksTile } from "../components/admin/DucksTile";
import { OrdersTile } from "../components/admin/OrdersTile";
import { PrintersTile } from "../components/admin/PrintersTile";
import { HeldTile } from "../components/admin/HeldTile";
import { CatalogGapsTile } from "../components/admin/CatalogGapsTile";
import { WebsiteTile } from "../components/admin/WebsiteTile";
import { GithubTile } from "../components/admin/GithubTile";
import { LogsTile } from "../components/admin/LogsTile";
import { GateHistoryTile } from "../components/admin/AccountsTile";
import { AccountsManager, PickHint } from "../components/admin/accounts/AccountsManager";
import { StaffLanding } from "../components/admin/StaffLanding";
import { AlertsTab } from "../components/admin/AlertsTab";
import { AdsTile } from "../components/admin/AdsTile";

const tabFromHash = () => {
  const h = (window.location.hash || "").replace("#", "");
  return TABS.some(([k]) => k === h) ? h : "overview";
};

// Each tab is one screen on a laptop (lg+): the grid fills the space below the header, lists scroll inside tiles.
const Grid = ({ testid, className, children }) => (
  <div data-testid={testid} className={`grid gap-3 lg:h-full ${className}`}>{children}</div>
);

const Cell = ({ span, children }) => <div className={`min-h-0 ${span}`}>{children}</div>;

const MoneyTab = ({ period, badges }) => {
  const s = useDash(`/dash/money?period=${period}`);
  return (
    <TileState state={s}>
      {s.data && (
        <Grid testid="dash-money" className="lg:grid-cols-12 lg:grid-rows-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <Cell span="lg:col-span-7"><AiCostsTile ai={s.data.ai_costs} tone={badges.money} index={0} /></Cell>
          <Cell span="lg:col-span-5"><SalesTile sales={s.data.sales} index={1} /></Cell>
          <Cell span="lg:col-span-12"><AdsTile index={2} /></Cell>
        </Grid>
      )}
    </TileState>
  );
};

const OperationsTab = () => {
  const s = useDash("/dash/operations");
  const [printers, setPrinters] = useState(null);
  useEffect(() => setPrinters(null), [s.data]);
  const d = s.data;
  return (
    <TileState state={s}>
      {d && (
        <Grid testid="dash-operations" className="lg:grid-cols-2 lg:grid-rows-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.8fr)]">
          <DucksTile ducks={d.ducks} tone={d.ducks.stuck.length ? "red" : null} index={0} />
          <OrdersTile orders={d.orders} tone={d.orders.to_print ? "amber" : null} index={1} />
          <PrintersTile printers={printers || d.printers} onSaved={setPrinters} index={2} />
          <HeldTile held={d.held} tone={d.held.held_now ? "red" : null} index={3} />
          <Cell span="min-w-0 lg:col-span-2"><CatalogGapsTile index={4} /></Cell>
        </Grid>
      )}
    </TileState>
  );
};

const SystemTab = ({ badges }) => {
  const s = useDash("/dash/system");
  return (
    <TileState state={s}>
      {s.data && (
        <Grid testid="dash-system" className="lg:grid-cols-12 lg:grid-rows-[minmax(0,1.25fr)_minmax(0,0.75fr)]">
          <Cell span="lg:col-span-4"><WebsiteTile w={s.data.website} models={s.data.models} tone={badges.system} index={0} /></Cell>
          <Cell span="lg:col-span-8 lg:row-span-2"><LogsTile index={1} /></Cell>
          <Cell span="lg:col-span-4"><GithubTile gh={s.data.github} index={2} /></Cell>
        </Grid>
      )}
    </TileState>
  );
};

const AccountsTab = ({ gated }) => {
  const s = useDash("/dash/accounts");
  const { user } = useAuth();
  const aside = (
    <div className="flex flex-col gap-3 lg:h-full">
      <PickHint />
      <div className="min-h-0 flex-1"><GateHistoryTile a={s.data} gated={gated} index={1} /></div>
    </div>
  );
  return (
    <TileState state={s}>
      {s.data && (
        <div data-testid="dash-accounts" className="lg:h-full">
          <AccountsManager actorRole={user.role} adminEmailsSet={s.data.admin_emails_set} aside={aside} />
        </div>
      )}
    </TileState>
  );
};

function Dashboard() {
  const [tab, setTab] = useState(tabFromHash);
  const [period, setPeriod] = useState("today");
  const overview = useDash(`/dash/overview?period=${period}`);
  useEffect(() => {
    const on = () => setTab(tabFromHash());
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  const go = useCallback((t) => {
    window.history.replaceState(null, "", `#${t}`);
    setTab(t);
  }, []);
  const o = overview.data;
  const badges = o?.badges || {};
  return (
    <div data-testid="admin-dashboard" className="flex min-h-screen flex-col bg-background lg:h-screen lg:overflow-hidden">
      <DashHeader gated={o?.gated} period={period} periods={o?.periods} onPeriod={setPeriod} updatedAt={o?.generated_at} onRefresh={overview.reload} />
      <DashTabs active={tab} badges={badges} onSelect={go} />
      <main data-testid={`dash-panel-${tab}`} className="min-h-0 flex-1 p-3 lg:p-4">
        {tab === "overview" && <TileState state={overview}>{o && <OverviewTab o={o} go={go} />}</TileState>}
        {tab === "alerts" && <AlertsTab />}
        {tab === "money" && <MoneyTab period={period} badges={badges} />}
        {tab === "operations" && <OperationsTab />}
        {tab === "system" && <SystemTab badges={badges} />}
        {tab === "accounts" && <AccountsTab gated={o?.gated} />}
      </main>
    </div>
  );
}

const DashboardOrLanding = () => {
  const { user } = useAuth();
  return ["admin", "owner"].includes(user?.role) ? <Dashboard /> : <StaffLanding />;
};

export default function AdminDashboard() {
  return (
    <AdminAreaGate min="staff">
      <DashboardOrLanding />
    </AdminAreaGate>
  );
}
